import { type DropDownMenuItemProps, DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import { strToUpperCaseFirst } from '@/Utility/utils';
import { ZnglyVideoMediaNameEnum } from '@/graphql/types';
import React, { useMemo } from 'react';
import { z } from 'zod';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const mediaTypeSearchSchema = z.object({
  mediaName: z.array(z.nativeEnum(ZnglyVideoMediaNameEnum)).nullish(),
});

const mediaTypeComponentProps = z.object({
  displayName: z.string().optional(),
});

type MediaTypeFilterProps = z.infer<typeof mediaTypeComponentProps>;

const mediaTypeArray = Object.values(ZnglyVideoMediaNameEnum)
  .map((item) => {
    let name = strToUpperCaseFirst(item);
    if (item === 'YOUTUBE') name = 'YouTube';
    if (item === 'PDF') name = 'PDF';
    if (item === 'TED') name = 'TED';
    if (item === 'MP4') name = 'MP4';
    if (item === 'SOUNDCLOUD') name = 'SoundCloud';

    if (item === 'UNDEFINED') return null;
    return [name, item] as [name: string, action: ZnglyVideoMediaNameEnum];
  })
  .filter((item) => !!item)
  .sort((a, b) => {
    if (!a || !b) return 0;
    if (a[0] > b[0]) return 1;
    if (a[0] < b[0]) return -1;
    return 0;
  });

const MediaTypeFilter = ({ displayName = 'Media' }: MediaTypeFilterProps) => {
  const { slowFilters, fastFilters, updateFilter, isUpdating } = __useFilterState(mediaTypeSearchSchema);

  const menuItems: DropDownMenuItemProps[] = useMemo(() => {
    return mediaTypeArray.map(([name, value]) => ({
      name,
      value,
      onClick: () => {
        updateFilter((prev) => {
          let newMediaTypes = [...(prev?.mediaName || []), value];
          if (prev?.mediaName?.includes(value)) {
            newMediaTypes = prev.mediaName.filter((type) => type !== value);
          }

          return {
            mediaName: newMediaTypes.length ? newMediaTypes : undefined,
          };
        });
      },
    })) as DropDownMenuItemProps[];
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className="z-btn-6"
        name={displayName}
        menuList={menuItems}
        type="multiple-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'mediaName',
          isSearchable: true,
          showCheckedOnTop: true,
        }}
      />
      <FilterBadge count={fastFilters?.mediaName?.length || 0} isUpdating={isUpdating} />
    </div>
  );
};

const MediaTypeFilterMemo = React.memo(MediaTypeFilter);

// Register the filter in the registry
export const FilterConfig_MediaType = {
  mediaType: {
    searchSchema: mediaTypeSearchSchema,
    component: MediaTypeFilterMemo,
    componentProps: mediaTypeComponentProps,
  },
} as const;
